import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Heart,
  LockKeyhole,
  MapPin,
  Maximize2,
  MessageSquareText,
  Play,
  SendHorizontal,
  ShieldCheck,
  X,
} from "lucide-react";
import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, json } from "../api";
import { useLocale } from "../App";
import type { Woman } from "../types";
import { Distance, PageHeader, PhoneShell } from "../components/Shell";
import { VipPromptModal } from "../components/VipPromptModal";

const platformIcon: Record<string, string> = {
  whatsapp: "◉",
  telegram: "➤",
  instagram: "◎",
};
const platformLabel: Record<string, string> = {
  whatsapp: "WhatsApp",
  telegram: "Telegram",
  instagram: "Instagram",
};
const contactOrder = ["whatsapp", "telegram", "instagram"];
export function WomanPage() {
  const { womanId = "" } = useParams();
  const lang = useLocale();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [active, setActive] = useState(0);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [vipPrompt, setVipPrompt] = useState(false);
  const query = useQuery({
    queryKey: ["woman", womanId, lang],
    queryFn: () => api<{ woman: Woman }>(`/women/${womanId}?lang=${lang}`),
  });
  const woman = query.data?.woman;
  const like = useMutation({
    mutationFn: () =>
      api<{ conversationId: string }>(`/likes/${womanId}`, { method: "POST" }),
    onSuccess: (result) =>
      navigate(`/${lang}/match/${womanId}/${result.conversationId}`),
  });
  const chat = useMutation({
    mutationFn: () =>
      api<{ conversationId: string }>("/conversations", json("POST", { womanId })),
    onSuccess: (result) => navigate(`/${lang}/chat/${result.conversationId}`),
  });
  if (!woman)
    return (
      <PhoneShell>
        <PageHeader
          title={t("details")}
          left={
            <button className="icon-button" onClick={() => navigate(-1)}>
              <ArrowLeft />
            </button>
          }
        />
        <div className="page-skeleton" />
      </PhoneShell>
    );
  const photo = woman.photos[active] ?? woman.photos[0];
  const contacts = woman.contacts.filter((item) =>
    ["whatsapp", "telegram", "instagram"].includes(item.platform),
  ).sort((a,b)=>contactOrder.indexOf(a.platform)-contactOrder.indexOf(b.platform));
  return (
    <PhoneShell className="woman-page">
      <PageHeader
        title={t("details")}
        left={
          <button className="icon-button" onClick={() => navigate(-1)}>
            <ArrowLeft />
          </button>
        }
        right={
          <button className="profile-chat-button" aria-label={t("messageHer")} title={t("messageHer")} disabled={chat.isPending} onClick={() => chat.mutate()}>
            <MessageSquareText />
            <SendHorizontal />
          </button>
        }
      />
      <div className="woman-scroll">
        <div className={`gallery-main ${photo?.locked ? "locked" : ""}`}>
          {(photo?.url||photo?.preview_url) && <button className="gallery-image-button" type="button" aria-label={photo.locked?t("photoLocked"):t("openFullPhoto")} onClick={() => photo.locked?setVipPrompt(true):setPreviewOpen(true)}><img src={photo.url??photo.preview_url??""} alt={woman.name} /><Maximize2/></button>}
          <button
            className="gallery-prev"
            onClick={() =>
              setActive(
                (active - 1 + woman.photos.length) % woman.photos.length,
              )
            }
          >
            <ChevronLeft />
          </button>
          <button className="gallery-next" onClick={() => setActive((active + 1) % woman.photos.length)}>
            <ChevronRight />
          </button>
          <span>
            {active + 1}/{woman.photos.length}
          </span>
          {photo?.locked && (
            <div className="locked-cover">
              <LockKeyhole />
              <strong>{t("photoLocked")}</strong>
            </div>
          )}
        </div>
        <h3 className="section-label">{t("photos")}</h3>
        <div className="thumb-row">
          {woman.photos.map((item, index) => (
            <button
              key={item.id}
              className={index === active ? "active" : ""}
              onClick={() => setActive(index)}
              aria-label={`${t("photos")} ${index+1}`}
            >
              {item.url||item.preview_url ? <img className={item.locked?"locked-media-preview":""} src={item.url??item.preview_url??""} alt="" /> : <LockKeyhole />}
              {item.locked?<LockKeyhole/>:null}
            </button>
          ))}
        </div>
        <section className="woman-copy">
          <h2>
            {woman.name}, {woman.age} <ShieldCheck />
          </h2>
          <p>
            <MapPin /> {woman.city} · {woman.district}　|　
            <Distance meters={woman.distance_m} />
          </p>
          <p>{woman.profession}</p>
          <p className="bio">{woman.bio}</p>
          <div className="interest-chips dark">
            {woman.interests.map((item) => (
              <span key={item}>{item}</span>
            ))}
          </div>
        </section>
        <h3 className="section-label">{t("videos")}</h3>
        <div className="video-list">
          {woman.videos.map((video,index)=><div className={`video-card ${video.locked?"locked":""}`} key={video.id}>
            {video.url?<video controls playsInline poster={video.preview_url??woman.photos[0]?.url??undefined} src={video.url}/>:<button type="button" onClick={()=>setVipPrompt(true)}><img className="locked-media-preview" src={video.preview_url??woman.photos[0]?.preview_url??woman.photos[0]?.url??""} alt=""/><div><LockKeyhole/><b>{t("videoLocked")}</b></div></button>}
            {!video.locked&&index===0?<Play/>:null}
          </div>)}
        </div>
        <h3 className="section-label">{t("contact")}</h3>
        <div className="contact-list">
          {contacts.map((contact) => (
            <button type="button" key={contact.platform} onClick={()=>contact.locked&&setVipPrompt(true)}>
              <i className={contact.platform}>
                {platformIcon[contact.platform]}
              </i>
            <span>{platformLabel[contact.platform]}</span>
              <small>
                {contact.locked ? t("contactLocked") : contact.handle}
              </small>
              {contact.locked && <LockKeyhole />}
            </button>
          ))}
        </div>
      </div>
      {previewOpen&&photo?.url&&!photo.locked?<div className="profile-photo-lightbox" role="dialog" aria-modal="true" aria-label={t("openFullPhoto")} onMouseDown={(event)=>{if(event.target===event.currentTarget)setPreviewOpen(false);}}>
        <button type="button" aria-label={t("closePhoto")} onClick={()=>setPreviewOpen(false)}><X/></button>
        <img src={photo.url} alt={woman.name}/>
        <span>{active+1}/{woman.photos.length}</span>
      </div>:null}
      <div className="sticky-like">
        <button onClick={() => navigate(`/${lang}/nearby`)}>
          <span>×</span>
          {t("skip")}
        </button>
        <button className="primary" onClick={() => like.mutate()}>
          <Heart fill="currentColor" />
          {t("like")}
        </button>
      </div>
      <VipPromptModal open={vipPrompt} onClose={()=>setVipPrompt(false)}/>
    </PhoneShell>
  );
}
